import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { money } from "./ownerCenterUi";
import { OWNER_RECURRING_FREQUENCIES, recurringFrequencyLabel } from "./ownerRecurringUi";

function directionFromTransaction(transaction) {
  if (transaction?.type) return transaction.type;
  if (transaction?.kind === "finance_income") return "income";
  if (transaction?.kind === "finance_expense") return "expense";
  return "";
}

function titleFromTransaction(transaction) {
  return transaction?.description || transaction?.title || transaction?.category || (directionFromTransaction(transaction) === "income" ? "Ingreso recurrente" : "Pago recurrente");
}

function idOf(value) {
  return String(value?.id || value?._id || value || "");
}

function ymd(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
}

function occurrenceLabel(row) {
  if (!row?.dueAt) return row?.periodKey || "Ocurrencia";
  return new Intl.DateTimeFormat("es-AR", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(row.dueAt));
}

export default function OwnerRecurringFromTransactionDialog({ open, onClose, transaction = null, recurring = null, onSaved }) {
  const [mode, setMode] = React.useState("new");
  const [planId, setPlanId] = React.useState("");
  const [occurrenceKey, setOccurrenceKey] = React.useState("");
  const [occurrences, setOccurrences] = React.useState([]);
  const [expectedAmount, setExpectedAmount] = React.useState("");
  const [loadingOccurrences, setLoadingOccurrences] = React.useState(false);
  const [frequency, setFrequency] = React.useState("monthly");
  const [name, setName] = React.useState("");
  const [amountMode, setAmountMode] = React.useState("fixed");
  const [endDate, setEndDate] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");

  const direction = directionFromTransaction(transaction);
  const currency = transaction?.currency || "ARS";
  const plans = (recurring?.plans || []).filter((plan) => plan.effectiveStatus === "active" && plan.direction === direction && plan.currency === currency);
  const selectedPlan = plans.find((plan) => idOf(plan) === String(planId));
  const selectedOccurrence = occurrences.find((row) => row.periodKey === occurrenceKey);

  React.useEffect(() => {
    if (!open) return;
    setMode("new");
    setPlanId("");
    setOccurrenceKey("");
    setOccurrences([]);
    setExpectedAmount("");
    setFrequency("monthly");
    setName(titleFromTransaction(transaction));
    setAmountMode("fixed");
    setEndDate("");
    setError("");
  }, [open, transaction]);

  React.useEffect(() => {
    if (!open || mode !== "existing" || !planId) {
      setOccurrences([]);
      setOccurrenceKey("");
      return undefined;
    }
    let cancelled = false;
    setLoadingOccurrences(true);
    setError("");
    ownerApi.listRecurringOccurrences(planId, { near: ymd(transaction?.occurredAt) }).then((response) => {
      if (cancelled) return;
      const rows = response?.occurrences || [];
      setOccurrences(rows);
      setOccurrenceKey(rows[0]?.periodKey || "");
      setExpectedAmount(rows[0]?.expectedAmount ? String(rows[0].expectedAmount) : "");
    }).catch((err) => {
      if (!cancelled) setError(formatApiError(err, "No pude cargar las ocurrencias pendientes de este acuerdo."));
    }).finally(() => {
      if (!cancelled) setLoadingOccurrences(false);
    });
    return () => { cancelled = true; };
  }, [open, mode, planId, transaction]);

  const submit = async () => {
    const transactionId = transaction?.entityId || transaction?.id || transaction?._id;
    if (!transactionId) return;
    setSaving(true);
    setError("");
    try {
      await ownerApi.linkFinanceTransactionRecurring(transactionId, mode === "existing"
        ? { planId, occurrenceKey, ...(selectedPlan?.amountMode === "variable" && expectedAmount ? { expectedAmount: Number(expectedAmount) } : {}) }
        : { name, amountMode, frequency, endDate: endDate || null });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(formatApiError(err, "No pude vincular este movimiento al acuerdo recurrente."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 1000, pb: 1 }}>Convertir movimiento en recurrente</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: .5 }}>
          <Box sx={{ p: 1.4, borderRadius: 2.4, bgcolor: "rgba(var(--wp-panel-ink-rgb,25,36,47),.04)" }}>
            <Typography sx={{ fontWeight: 950 }}>{titleFromTransaction(transaction)}</Typography>
            <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: ".82rem", mt: .2 }}>
              El movimiento real queda intacto. Sólo se relaciona con una regla recurrente y una ocurrencia concreta.
            </Typography>
            {transaction?.amount !== null && transaction?.amount !== undefined ? <Typography sx={{ fontWeight: 1000, mt: .7 }}>{money(transaction.amount, currency)}</Typography> : null}
          </Box>

          {error ? <Alert severity="error">{error}</Alert> : null}

          <FormControl fullWidth>
            <InputLabel>Qué querés hacer</InputLabel>
            <Select label="Qué querés hacer" value={mode} onChange={(event) => setMode(event.target.value)}>
              <MenuItem value="new">Crear un acuerdo recurrente nuevo</MenuItem>
              {plans.length ? <MenuItem value="existing">Vincular a un acuerdo existente</MenuItem> : null}
            </Select>
          </FormControl>

          {mode === "existing" ? (
            <>
              <FormControl fullWidth>
                <InputLabel>Acuerdo recurrente</InputLabel>
                <Select label="Acuerdo recurrente" value={planId} onChange={(event) => setPlanId(event.target.value)}>
                  {plans.map((plan) => <MenuItem key={idOf(plan)} value={idOf(plan)}>{plan.name} · {recurringFrequencyLabel(plan.frequency)}</MenuItem>)}
                </Select>
              </FormControl>
              {selectedPlan ? (
                <FormControl fullWidth disabled={loadingOccurrences || !occurrences.length}>
                  <InputLabel>Ocurrencia pendiente</InputLabel>
                  <Select label="Ocurrencia pendiente" value={occurrenceKey} onChange={(event) => { const key = event.target.value; setOccurrenceKey(key); const row = occurrences.find((item) => item.periodKey === key); setExpectedAmount(row?.expectedAmount ? String(row.expectedAmount) : ""); }}>
                    {occurrences.filter((row) => Number(row.pendingAmount || 0) > .005).map((row) => <MenuItem key={row.periodKey} value={row.periodKey}>{occurrenceLabel(row)} · faltan {money(row.pendingAmount, currency)}</MenuItem>)}
                  </Select>
                </FormControl>
              ) : null}
              {selectedOccurrence ? <Alert severity={Number(transaction?.amount || 0) > Number(selectedOccurrence.pendingAmount || 0) + .005 ? "warning" : "info"}>Esperado {money(selectedOccurrence.expectedAmount, currency)} · aplicado {money(selectedOccurrence.paidAmount, currency)} · pendiente {money(selectedOccurrence.pendingAmount, currency)}. Vincular este movimiento no crea otro.</Alert> : null}
              {selectedPlan?.amountMode === "variable" && selectedOccurrence ? <TextField fullWidth type="number" label={`Esperado para este período · ${currency}`} value={expectedAmount} onChange={(event) => setExpectedAmount(event.target.value)} helperText="Podés confirmar el esperado antes de vincular; no cambia el movimiento real." /> : null}
              {selectedPlan && !loadingOccurrences && !occurrences.some((row) => Number(row.pendingAmount || 0) > .005) ? <Alert severity="info">No hay una ocurrencia abierta cercana para este movimiento. Revisá la vigencia o elegí otro acuerdo.</Alert> : null}
            </>
          ) : (
            <>
              <TextField fullWidth label="Nombre" value={name} onChange={(event) => setName(event.target.value)} placeholder="Ej.: Alquiler" />
              <FormControl fullWidth>
                <InputLabel>Frecuencia</InputLabel>
                <Select label="Frecuencia" value={frequency} onChange={(event) => setFrequency(event.target.value)}>
                  {OWNER_RECURRING_FREQUENCIES.map((row) => <MenuItem key={row.value} value={row.value}>{row.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Cómo se comporta el monto</InputLabel>
                <Select label="Cómo se comporta el monto" value={amountMode} onChange={(event) => setAmountMode(event.target.value)}>
                  <MenuItem value="fixed">Fijo · usar este monto como referencia por ocurrencia</MenuItem>
                  <MenuItem value="variable">Variable · aprender del promedio de registros reales</MenuItem>
                </Select>
              </FormControl>
              <TextField fullWidth type="date" label="Finaliza (opcional)" InputLabelProps={{ shrink: true }} value={endDate} onChange={(event) => setEndDate(event.target.value)} />
            </>
          )}

          <Typography sx={{ color: "text.secondary", fontWeight: 700, fontSize: ".76rem" }}>
            Al crear uno nuevo, la fecha de este movimiento se usa como inicio y primer vencimiento. Semanal avanza cada 7 días; mensual, trimestral y anual preservan el día del ancla.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.3 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cancelar</Button>
        <Button variant="contained" startIcon={<RepeatRoundedIcon />} onClick={submit} disabled={saving || (mode === "existing" ? !planId || !occurrenceKey || loadingOccurrences || Number(transaction?.amount || 0) > Number(selectedOccurrence?.pendingAmount || 0) + .005 || (selectedPlan?.amountMode === "variable" && !(Number(expectedAmount) > 0)) : !name.trim())} sx={{ textTransform: "none", fontWeight: 950 }}>
          {saving ? "Guardando…" : mode === "existing" ? "Vincular ocurrencia" : "Activar recurrente"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
